import React, {
    ComponentType,
    CSSProperties,
    useMemo,
    useRef,
    useEffect,
    useCallback,
    useLayoutEffect,
} from 'react';
import styles from './additional-info-renderer.module.css';
import { useVirtualizer } from '@tanstack/react-virtual';
import { observer } from 'mobx-react-lite';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import { AdditionalInfoView, TempAdditionalInfoViewWithPinFields } from '../types';
import { PinnedNarrativeRow } from '../event-panel-additional-infos/components/pinned-narrative-row';
import { PinnedDividerRow } from '../event-panel-additional-infos/components/pinned-divider-row';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { VirtualizerContext } from './use-row-virtualizer';
import { useUpdateVisibleAsRead } from './use-update-visible-read';
import { useScrollAnchor } from './use-scroll-anchor';
import { useEstimateRowSize } from './use-estimate-row-size';

type PinnedDividerItem = { type: 'PINNED_DIVIDER'; id: -1 };
type PinnedRowItem = TempAdditionalInfoViewWithPinFields & { _rowType: 'pinned' };
type ChronologicalRowItem = AdditionalInfoView & { _rowType?: undefined };
type VirtualizerItem = PinnedRowItem | PinnedDividerItem | ChronologicalRowItem;

// Row props that must be provided to the renderer
interface AdditionalInfoRowProps {
    currentUserId?: number;
    canDeleteInfos?: boolean;
    isPinned?: boolean;
    isScheduledEvent?: boolean;
    onOpenEvent?: (id: number) => void;
}
export interface AdditionalInfoRendererProps extends AdditionalInfoRowProps {
    height?: CSSProperties['height'];
    width?: CSSProperties['width'];
    markVisibleAsRead?: boolean;
    rowComponent: ComponentType<
        {
            data: AdditionalInfoView;
            index: number;
            agencyEventNumberToIdMap?: Record<string, number>;
            isProQA?: boolean;
        } & AdditionalInfoRowProps
    >;
    loadingComponent?: ComponentType;
    addonComponent?: ComponentType<{ height?: number; width?: number }>;
    onAllRead?: (id: number) => void;
    overscan?: number;
    forceReload?: boolean;
    isReadOnlyMode?: boolean;
    isFirstResponder?: boolean;
}

export const AdditionalInfoRenderer = observer(
    ({
        rowComponent: RowComponent,
        loadingComponent: LoadingComponent,
        addonComponent: AddonComponent,
        markVisibleAsRead = true,
        onAllRead,
        overscan = 0,
        forceReload,
        ...rest
    }: AdditionalInfoRendererProps) => {
        const scrollContainerRef = useRef<HTMLDivElement | null>(null);
        const {
            list: data,
            agencyEventNumberToIdMap,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            pageSize,
            pageFetchFailed,
            initialSocketConnectionMade,
            reloadPage,
            isPageReloading,
            visiblePinnedEntries,
            showPinnedDivider,
            hiddenPinnedCount,
            isPinnedSectionExpanded,
            togglePinnedSection,
        } = useAdditionalInfos();

        const { rollOutCadNarrativeFilteringAndFocusImprovementsEnabled } = useEnabledFeatures();

        const pinnedRows: VirtualizerItem[] = useMemo(
            () =>
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
                    ? visiblePinnedEntries.map((entry) => ({
                          ...entry,
                          _rowType: 'pinned' as const,
                      }))
                    : [],
            [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, visiblePinnedEntries]
        );

        const divider: VirtualizerItem[] = useMemo(
            () =>
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && showPinnedDivider
                    ? [{ type: 'PINNED_DIVIDER' as const, id: -1 as const }]
                    : [],
            [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, showPinnedDivider]
        );

        // data.length is intentionally included: data is a MobX IObservableArray
        // whose reference stays stable across mutations, so useMemo won't re-run
        // without it. data.length changes when items are added/removed.
        const combinedData: VirtualizerItem[] = useMemo(
            () => [...pinnedRows, ...divider, ...data],
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [pinnedRows, divider, data, data.length]
        );

        const getItemKey = useCallback(
            (index: number) => {
                const isLoaderRow = index === combinedData.length;
                if (isLoaderRow) return index;
                const item = combinedData[index];
                if ('type' in item && item.type === 'PINNED_DIVIDER') return 'pinned-divider';
                return item.id;
            },
            [combinedData]
        );

        const { estimateRowSize } = useEstimateRowSize(data, scrollContainerRef);

        const rowVirtualizer = useVirtualizer({
            count: hasNextPage ? combinedData.length + 1 : combinedData.length,
            getItemKey,
            getScrollElement: () => scrollContainerRef.current,
            estimateSize: estimateRowSize,
            overscan,
        });

        useScrollAnchor({
            data,
            rowVirtualizer,
        });

        const virtualItems = rowVirtualizer.getVirtualItems();

        const lastVirtualItem = virtualItems[virtualItems.length - 1];
        const chronologicalStartIndex = pinnedRows.length + divider.length;
        useEffect(
            function triggerNextPageFetch() {
                if (!lastVirtualItem) {
                    return;
                }

                if (
                    lastVirtualItem.index >= chronologicalStartIndex + data.length - 1 &&
                    hasNextPage &&
                    !isFetchingNextPage &&
                    !pageFetchFailed &&
                    initialSocketConnectionMade
                ) {
                    fetchNextPage();
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                chronologicalStartIndex,
                data.length,
                fetchNextPage,
                hasNextPage,
                initialSocketConnectionMade,
                isFetchingNextPage,
                pageSize,
                lastVirtualItem,
            ]
        );

        useLayoutEffect(() => {
            // Something is causing the scrollElement div scrollTop (the div which contains the narrative scrollbar) to change
            // to 0 when a different event window is focussed and then this event window is focussed. The rowVirtualizer is not
            // aware of this and its scrollOffset remains it was previously (i.e. not 0). This causes the scrollContent div to
            // scroll to a blank area. This is a hack which causes the rowVirtualizer to scroll the scrollContent div to the offset
            // that the rowVirtualizer thinks it's already at after rendering.
            if (
                scrollContainerRef.current !== null &&
                rowVirtualizer &&
                scrollContainerRef.current.scrollTop !== rowVirtualizer.scrollOffset
            ) {
                rowVirtualizer.scrollToOffset(rowVirtualizer.scrollOffset, {
                    align: 'start',
                });
            }
        });

        const totalSize = rowVirtualizer.getTotalSize();
        const fullHeightScrollContentStyle = useMemo(
            (): CSSProperties => ({
                height: totalSize,
                width: '100%',
                position: 'relative',
            }),
            [totalSize]
        );

        const virtualItemsStart = virtualItems[0]?.start;
        const translateVirtualItemsStyle = useMemo(
            (): CSSProperties =>
                virtualItemsStart === undefined
                    ? {}
                    : {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualItemsStart}px)`,
                      },
            [virtualItemsStart]
        );

        useUpdateVisibleAsRead(markVisibleAsRead, rowVirtualizer);

        useEffect(() => {
            if (forceReload) {
                reloadPage();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [forceReload]);

        return (
            <VirtualizerContext.Provider value={rowVirtualizer}>
                <div className={styles['addon-component-wrapper']}>
                    <div
                        ref={scrollContainerRef}
                        className={styles['scroll-container']}
                        data-testid="ADDITIONAL_INFOS_SCROLL_CONTAINER"
                    >
                        <div style={fullHeightScrollContentStyle}>
                            <div style={translateVirtualItemsStyle}>
                                {isPageReloading ? (
                                    <LoadingIndicator />
                                ) : (
                                    virtualItems.map((virtualRow) => {
                                        const isLoaderRow =
                                            virtualRow.index > combinedData.length - 1;
                                        return (
                                            <div
                                                key={virtualRow.key}
                                                data-index={virtualRow.index}
                                                ref={rowVirtualizer.measureElement}
                                            >
                                                {!isLoaderRow
                                                    ? (() => {
                                                          const item = combinedData[
                                                              virtualRow.index
                                                          ] as VirtualizerItem;
                                                          if (
                                                              'type' in item &&
                                                              item.type === 'PINNED_DIVIDER'
                                                          ) {
                                                              return (
                                                                  <PinnedDividerRow
                                                                      hiddenCount={
                                                                          hiddenPinnedCount
                                                                      }
                                                                      isExpanded={
                                                                          isPinnedSectionExpanded
                                                                      }
                                                                      onToggle={() => {
                                                                          togglePinnedSection();
                                                                          // Frame 1: invalidate cached row heights
                                                                          // Frame 2: scroll to top after virtualizer re-renders
                                                                          requestAnimationFrame(
                                                                              () => {
                                                                                  rowVirtualizer.measure();
                                                                                  requestAnimationFrame(
                                                                                      () => {
                                                                                          rowVirtualizer.scrollToOffset(
                                                                                              0
                                                                                          );
                                                                                      }
                                                                                  );
                                                                              }
                                                                          );
                                                                      }}
                                                                  />
                                                              );
                                                          }
                                                          if (
                                                              '_rowType' in item &&
                                                              item._rowType === 'pinned'
                                                          ) {
                                                              const pinnedIndex = pinnedRows.findIndex(
                                                                  (r) => r.id === item.id
                                                              );
                                                              const isLastPinned =
                                                                  pinnedIndex ===
                                                                  pinnedRows.length - 1;
                                                              return (
                                                                  <PinnedNarrativeRow
                                                                      data={
                                                                          item as TempAdditionalInfoViewWithPinFields
                                                                      }
                                                                      isFirst={pinnedIndex === 0}
                                                                      isLast={
                                                                          isLastPinned &&
                                                                          !showPinnedDivider
                                                                      }
                                                                      isExpanded={
                                                                          isPinnedSectionExpanded
                                                                      }
                                                                      currentUserId={
                                                                          rest.currentUserId
                                                                      }
                                                                      isScheduledEvent={
                                                                          rest.isScheduledEvent
                                                                      }
                                                                      isFirstResponder={
                                                                          rest.isFirstResponder
                                                                      }
                                                                      agencyEventNumberToIdMap={
                                                                          agencyEventNumberToIdMap
                                                                      }
                                                                      onOpenEvent={rest.onOpenEvent}
                                                                  />
                                                              );
                                                          }
                                                          return (
                                                              <RowComponent
                                                                  data={item as AdditionalInfoView}
                                                                  agencyEventNumberToIdMap={
                                                                      agencyEventNumberToIdMap
                                                                  }
                                                                  index={virtualRow.index}
                                                                  {...rest}
                                                              />
                                                          );
                                                      })()
                                                    : !pageFetchFailed &&
                                                      (LoadingComponent ? (
                                                          <LoadingComponent />
                                                      ) : (
                                                          <LoadingIndicator />
                                                      ))}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                    {AddonComponent && <AddonComponent />}
                </div>
            </VirtualizerContext.Provider>
        );
    }
);

export default AdditionalInfoRenderer;
